import React from 'react';
import PropTypes from 'prop-types';
import InlineSvg from 'react-inlinesvg';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { ICONS, ILLUSTRATIONS } from 'constants/icons';
import Message from 'components/Message';
import { googleMessages, iCalMessages } from './CalSyncTutorialModal.messages';

const { element } = bem('calsync-tutorial-modal');

const ICalFirstScreen = () => (
  <>
    <Message className={element('title')} as="h1" message={iCalMessages.stepOne.title} />
    <Message className={element('text')} as="p" message={iCalMessages.stepOne.paragraphOne} />
    <div className={element('illustration')}>
      <InlineSvg src={ILLUSTRATIONS.calSync.tutorial.screenOne} />
    </div>
    <Message className={element('text')} as="p" message={iCalMessages.stepOne.paragraphTwo} />
  </>
);

const ICalSecondScreen = ({ venueId }) => (
  <>
    <Message className={element('title')} as="h1" message={iCalMessages.stepTwo.title} />
    <Message
      className={element('text')}
      as="p"
      message={iCalMessages.stepTwo.paragraphOne}
      values={{
        a: chunk => (
          <a href={makePath.manageServicesPractitioners({ venueId })} target="_blank" rel="noreferrer">
            {chunk}
          </a>
        ),
      }}
    />
    <div className={element('illustration')}>
      <InlineSvg src={ILLUSTRATIONS.calSync.tutorial.screenTwoICal} />
    </div>
    <Message className={element('text', 'mb-regular')} as="p" message={iCalMessages.stepTwo.paragraphTwo} />
    <Message className={element('text')} as="p" message={iCalMessages.stepTwo.paragraphThree} />
  </>
);

ICalSecondScreen.propTypes = {
  venueId: PropTypes.number,
};

const ICalThirdScreen = () => (
  <>
    <Message className={element('title')} as="h1" message={iCalMessages.stepThree.title} />
    <Message className={element('text')} as="p" message={iCalMessages.stepThree.paragraphOne} />
    <div className={element('illustration')}>
      <InlineSvg src={ILLUSTRATIONS.calSync.tutorial.screenThree} />
    </div>
  </>
);

const ICalFourthScreen = () => (
  <>
    <Message className={element('title')} as="h1" message={iCalMessages.stepFour.title} />
    <Message className={element('text')} as="p" message={iCalMessages.stepFour.paragraphOne} />
    <div className={element('illustration')}>
      <InlineSvg src={ILLUSTRATIONS.calSync.tutorial.screenFourICal} />
    </div>
    <Message
      className={element('text')}
      as="p"
      message={iCalMessages.stepFour.paragraphTwo}
      values={{ emphasis: chunk => <span className="text--semibold">{chunk}</span> }}
    />
    <Message
      className={element('text', 'mt-huge')}
      as="p"
      message={iCalMessages.stepFour.paragraphThree}
      values={{
        a: chunk => (
          <a
            href="https://partnersupport.classpass.com/help/s/article/calendarsync-with-ical"
            className="text--semibold"
            target="_blank"
            rel="noreferrer">
            {chunk}
            <InlineSvg className="ml-tiny" src={ICONS.openExternal} />
          </a>
        ),
      }}
    />
  </>
);

const GoogleFirstScreen = () => (
  <>
    <Message className={element('title')} as="h1" message={googleMessages.stepOne.title} />
    <Message className={element('text')} as="p" message={googleMessages.stepOne.paragraphOne} />
    <div className={element('illustration')}>
      <InlineSvg src={ILLUSTRATIONS.calSync.tutorial.screenOne} />
    </div>
    <Message className={element('text')} as="p" message={googleMessages.stepOne.paragraphTwo} />
  </>
);

const GoogleSecondScreen = () => (
  <>
    <Message className={element('title')} as="h1" message={googleMessages.stepTwo.title} />
    <Message className={element('text')} as="p" message={googleMessages.stepTwo.paragraphOne} />
    <div className={element('illustration')}>
      <InlineSvg src={ILLUSTRATIONS.calSync.tutorial.screenTwo} />
    </div>
    <Message className={element('text')} as="p" message={googleMessages.stepTwo.paragraphTwo} />
    <div className="mt-large">
      <Message
        as="a"
        target="_blank"
        rel="noreferrer"
        href="https://support.google.com/calendar/answer/37095"
        message={googleMessages.stepTwo.learnHow}
      />
    </div>
  </>
);

const GoogleThirdScreen = () => (
  <>
    <Message className={element('title')} as="h1" message={googleMessages.stepThree.title} />
    <Message className={element('text')} as="p" message={googleMessages.stepThree.paragraphOne} />
    <div className={element('illustration')}>
      <InlineSvg src={ILLUSTRATIONS.calSync.tutorial.screenThree} />
    </div>
  </>
);

const GoogleFourthScreen = () => (
  <>
    <Message className={element('title')} as="h1" message={googleMessages.stepFour.title} />
    <Message className={element('text')} as="p" message={googleMessages.stepFour.paragraphOne} />
    <div className={element('illustration')}>
      <InlineSvg src={ILLUSTRATIONS.calSync.tutorial.screenFour} />
    </div>
    <Message
      className={element('text')}
      as="p"
      message={googleMessages.stepFour.paragraphTwo}
      values={{
        a: chunk => (
          <a
            href="https://partnersupport.classpass.com/help/s/article/calendarsync-with-google-calendar"
            className="text--semibold"
            target="_blank"
            rel="noreferrer">
            {chunk}
            <InlineSvg className="ml-tiny" src={ICONS.openExternal} />
          </a>
        ),
      }}
    />
  </>
);

const iCalScreens = [ICalFirstScreen, ICalSecondScreen, ICalThirdScreen, ICalFourthScreen];
const googleScreens = [GoogleFirstScreen, GoogleSecondScreen, GoogleThirdScreen, GoogleFourthScreen];

export const getCurrentStepScreen = ({ currentStep, flowType }) => {
  if (flowType === 'google') {
    return googleScreens[currentStep];
  }

  if (flowType === 'ical') {
    return iCalScreens[currentStep];
  }

  return iCalScreens[0];
};
